import { WizardPhaseKey } from '../../views/restaurant-onboarding/phases';

/**
 * Dashboard card states for the partner-onboarding wizard (FOO-6351 C7).
 *
 * A restaurant card reflects where its onboarding stands, driven by the SAME
 * progress model the wizard uses (C0) — no divergent source:
 *   - `Live`       — live orders are enabled. Enabling live orders is the wizard's
 *                    terminal phase (`WizardPhaseKey.GoLive` → the `EnableLiveOrders`
 *                    onboarding step), so a live company has completed onboarding.
 *   - `SettingUp`  — a persisted restaurant whose onboarding is not yet finished
 *                    (live orders not enabled). The card offers "Continue Setup",
 *                    which resumes the wizard at its first-incomplete phase.
 *   - `null`       — no state badge (e.g. an in-memory draft not yet created, so
 *                    it has no id and onboarding has not begun on the server).
 *
 * The list payload (Dashboard `CompanyResource`) exposes `has_live_orders` but not
 * per-company onboarding steps, and the onboarding-steps endpoint is scoped to the
 * current company. `has_live_orders` — the go-live phase's completion signal — is
 * therefore the single source of truth for the Live vs Setting-up split in a list.
 */
export const CompanyCardStatus = {
  Live: 'live',
  SettingUp: 'setting_up',
};

/**
 * The wizard phase whose completion the "Live" badge reflects. Documents the tie
 * back to the shared phase model so the derivation below is not a magic string.
 */
export const LIVE_COMPLETION_PHASE = WizardPhaseKey.GoLive;

/**
 * Derive a company's onboarding card status from its server fields.
 *
 * @param {{ id?: number|string, has_live_orders?: boolean }|null|undefined} company
 * @param {{ isDraft?: boolean }} [options] `isDraft` marks an in-memory company
 *   being created (no server record yet) — such a card shows no onboarding state.
 * @returns {('live'|'setting_up'|null)}
 */
export function deriveCompanyCardStatus(company, { isDraft = false } = {}) {
  if (isDraft || !company?.id) {
    return null;
  }

  if (company.has_live_orders) {
    return CompanyCardStatus.Live;
  }

  return CompanyCardStatus.SettingUp;
}
